import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Gift, Plus, Pencil, Trash2, Package, Coins, ShoppingBag, Loader2 } from 'lucide-react';

export default function AdminRewardsPage({ embedded = false }) {
  const { api } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    points_cost: 100,
    inventory_qty: 10,
    variants: [],
    fulfillment_method: 'pickup',
    active: true
  });
  const [variantInput, setVariantInput] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rewardsRes, redemptionsRes] = await Promise.all([
        api.get('/rewards/?active_only=false'),
        api.get('/rewards/redemptions')
      ]);
      setRewards(rewardsRes.data?.results || rewardsRes.data || []);
      setRedemptions(redemptionsRes.data?.results || redemptionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load rewards');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReward) {
        await api.put(`/rewards/${editingReward.id}/`, formData);
        toast.success('Reward updated');
      } else {
        await api.post('/rewards/', formData);
        toast.success('Reward created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save reward');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reward?')) return;
    try {
      await api.delete(`/rewards/${id}/`);
      toast.success('Reward deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete reward');
    }
  };

  const updateRedemptionStatus = async (redemptionId, status) => {
    try {
      await api.put(`/rewards/redemptions/${redemptionId}/status`, { status });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image_url: '',
      points_cost: 100,
      inventory_qty: 10,
      variants: [],
      fulfillment_method: 'pickup',
      active: true
    });
    setVariantInput('');
    setEditingReward(null);
  };

  const openEditDialog = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      description: reward.description,
      image_url: reward.image_url || '',
      points_cost: reward.points_cost,
      inventory_qty: reward.inventory_qty,
      variants: reward.variants || [],
      fulfillment_method: reward.fulfillment_method || 'pickup',
      active: reward.active
    });
    setDialogOpen(true);
  };

  const addVariant = () => {
    if (variantInput.trim()) {
      setFormData({
        ...formData,
        variants: [...formData.variants, variantInput.trim()]
      });
      setVariantInput('');
    }
  };

  const removeVariant = (index) => {
    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index)
    });
  };

  const stats = {
    totalRewards: rewards.length,
    activeRewards: rewards.filter(r => r.active).length,
    totalInventory: rewards.reduce((sum, r) => sum + r.inventory_qty, 0),
    pendingRedemptions: redemptions.filter(r => r.status === 'requested').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-rewards-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold">Rewards Store</h1>
          <p className="text-muted-foreground">Manage rewards and redemptions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-reward-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Reward
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingReward ? 'Edit Reward' : 'Add New Reward'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  placeholder="Coding Sticker Pack"
                  required 
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  placeholder="A pack of 5 coding-themed stickers"
                  required 
                />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input 
                  value={formData.image_url} 
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})} 
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Points Cost *</Label>
                  <Input 
                    type="number"
                    value={formData.points_cost} 
                    onChange={(e) => setFormData({...formData, points_cost: parseInt(e.target.value)})} 
                    required 
                  />
                </div>
                <div>
                  <Label>Inventory Qty</Label>
                  <Input 
                    type="number"
                    value={formData.inventory_qty} 
                    onChange={(e) => setFormData({...formData, inventory_qty: parseInt(e.target.value)})} 
                  />
                </div>
              </div>
              <div>
                <Label>Variants (sizes, colors, etc.)</Label>
                <div className="flex gap-2 mb-2">
                  <Input 
                    value={variantInput}
                    onChange={(e) => setVariantInput(e.target.value)}
                    placeholder="Add variant (e.g., Small, Medium, Large)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVariant())}
                  />
                  <Button type="button" variant="outline" onClick={addVariant}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.variants.map((v, i) => (
                    <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeVariant(i)}>
                      {v} ×
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.active} 
                  onCheckedChange={(checked) => setFormData({...formData, active: checked})} 
                />
                <Label>Active (visible in store)</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingReward ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Gift className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{stats.totalRewards}</p>
            <p className="text-sm text-muted-foreground">Total Rewards</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <ShoppingBag className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold">{stats.activeRewards}</p>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Package className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold">{stats.totalInventory}</p>
            <p className="text-sm text-muted-foreground">Total Inventory</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Coins className="h-8 w-8 mx-auto mb-2 text-orange-600" />
            <p className="text-2xl font-bold">{stats.pendingRedemptions}</p>
            <p className="text-sm text-muted-foreground">Pending Redemptions</p>
          </CardContent>
        </Card>
      </div>

      {/* Rewards Table */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle>Rewards Catalog</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reward</TableHead>
                <TableHead>Points</TableHead>
                <TableHead>Inventory</TableHead>
                <TableHead>Variants</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rewards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No rewards found. Add your first reward above.
                  </TableCell>
                </TableRow>
              ) : (
                rewards.map((reward) => (
                  <TableRow key={reward.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {reward.image_url ? (
                          <img src={reward.image_url} alt={reward.name} className="w-10 h-10 rounded object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                            <Gift className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{reward.name}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">{reward.description}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{reward.points_cost} pts</Badge>
                    </TableCell>
                    <TableCell>
                      <span className={reward.inventory_qty <= 5 ? 'text-red-600 font-medium' : ''}>
                        {reward.inventory_qty}
                      </span>
                    </TableCell>
                    <TableCell>
                      {reward.variants?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {reward.variants.slice(0, 3).map((v, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
                          ))}
                          {reward.variants.length > 3 && (
                            <Badge variant="secondary" className="text-xs">+{reward.variants.length - 3}</Badge>
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={reward.active ? 'default' : 'secondary'}>
                        {reward.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(reward)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(reward.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Redemptions */}
      {redemptions.length > 0 && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle>Recent Redemptions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reward</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Variant</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redemptions.slice(0, 10).map((redemption) => (
                  <TableRow key={redemption.id}>
                    <TableCell className="font-medium">{redemption.reward_name}</TableCell>
                    <TableCell>{redemption.learner?.first_name} {redemption.learner?.last_name}</TableCell>
                    <TableCell>{redemption.points_spent}</TableCell>
                    <TableCell>{redemption.variant || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={
                        redemption.status === 'fulfilled' ? 'default' :
                        redemption.status === 'approved' ? 'secondary' : 'outline'
                      }>
                        {redemption.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(redemption.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {redemption.status === 'requested' && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => updateRedemptionStatus(redemption.id, 'approved')}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateRedemptionStatus(redemption.id, 'fulfilled')}>
                            Fulfill
                          </Button>
                        </div>
                      )}
                      {redemption.status === 'approved' && (
                        <Button size="sm" variant="outline" onClick={() => updateRedemptionStatus(redemption.id, 'fulfilled')}>
                          Mark Fulfilled
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
